import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type {
  AdminActivityItemView,
  ApplicationPipelineView,
  AttentionItemView,
  DashboardMetricCard,
  FinancialSnapshotView,
  OverviewOperationalSummary,
  PermissionCode,
  SalesOperationalExtras,
  SupportOperationalExtras,
} from '@solar/contracts';
import { PERMISSIONS } from '@solar/contracts';

import houseHeroUrl from '../../images/dashboard-house-hero.png';
import {
  collectionRatePresentation,
  displayNameFromEmail,
  formatGreeting,
  formatNairaAmount,
  formatRelativeTime,
  humanizeStage,
  resolveAdminDeepLink,
} from '../../lib/admin-overview';
import { paths } from '../../routes/paths';

import styles from './AdminOverviewFidelity.module.css';

type IconName =
  | 'activity'
  | 'alert'
  | 'application'
  | 'arrow'
  | 'billing'
  | 'calendar'
  | 'check'
  | 'collections'
  | 'contract'
  | 'customer'
  | 'draft'
  | 'flag'
  | 'guidance'
  | 'mandate'
  | 'review'
  | 'spark'
  | 'submit';

function OverviewIcon({ name }: { name: IconName }): React.JSX.Element {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  switch (name) {
    case 'customer':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            {...common}
            d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
          />
        </svg>
      );
    case 'contract':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M7 3h8l4 4v14H7zM15 3v5h4M10 12h6M10 16h6" />
        </svg>
      );
    case 'application':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M6 3h9l4 4v14H6zM15 3v5h4M9 12h7M9 16h5" />
        </svg>
      );
    case 'mandate':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M3 10h18M5 10V8l7-4 7 4v2M6 10v7M10 10v7M14 10v7M18 10v7M3 20h18" />
        </svg>
      );
    case 'billing':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M4 6h16v12H4zM4 10h16M8 15h3" />
        </svg>
      );
    case 'calendar':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M4 5h16v16H4zM8 3v4M16 3v4M4 9h16M8 13h3M8 17h6" />
        </svg>
      );
    case 'collections':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            {...common}
            d="M12 2v20M17 6.5C17 4.57 14.76 3 12 3S7 4.57 7 6.5 9.24 10 12 10s5 1.57 5 3.5S14.76 17 12 17s-5-1.57-5-3.5"
          />
        </svg>
      );
    case 'draft':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M5 3h10l4 4v14H5zM15 3v5h4M9 13h6" />
        </svg>
      );
    case 'submit':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="m4 12 16-8-6 16-3-7zM11 13l4-4" />
        </svg>
      );
    case 'review':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle {...common} cx="11" cy="11" r="7" />
          <path {...common} d="m20 20-4-4M8 11l2 2 4-4" />
        </svg>
      );
    case 'alert':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            {...common}
            d="M10.3 3.7 2.5 18a2 2 0 0 0 1.75 3h15.5a2 2 0 0 0 1.75-3L13.7 3.7a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01"
          />
        </svg>
      );
    case 'check':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle {...common} cx="12" cy="12" r="9" />
          <path {...common} d="m8 12 2.6 2.6L16.5 9" />
        </svg>
      );
    case 'flag':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M5 21V4M5 5h11l-2 4 2 4H5" />
        </svg>
      );
    case 'activity':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M3 12h4l2.5-6 5 12 2.5-6h4" />
        </svg>
      );
    case 'guidance':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle {...common} cx="12" cy="12" r="9" />
          <path {...common} d="M9.5 9a2.5 2.5 0 0 1 4.9.7c0 1.8-2.4 2.2-2.4 4M12 18h.01" />
        </svg>
      );
    case 'arrow':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="M5 12h14M14 7l5 5-5 5" />
        </svg>
      );
    case 'spark':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...common} d="m13 2-8 12h7l-1 8 8-12h-7z" />
        </svg>
      );
  }
}

interface MetricPresentation {
  icon: IconName;
  support: string;
  tone: 'green' | 'blue' | 'amber' | 'red' | 'violet';
}

const METRIC_PRESENTATION: Readonly<Record<string, MetricPresentation>> = {
  applications_pending: {
    icon: 'application',
    support: 'Waiting in the review queue',
    tone: 'green',
  },
  customers_total: { icon: 'customer', support: 'Registered customer profiles', tone: 'blue' },
  subscriptions_active: {
    icon: 'contract',
    support: 'Repayment contracts in progress',
    tone: 'violet',
  },
  mandates_pending: { icon: 'mandate', support: 'Need provider activation', tone: 'amber' },
  billing_overdue: { icon: 'billing', support: 'Schedules requiring follow-up', tone: 'red' },
  notifications_failed: { icon: 'alert', support: 'Deliveries requiring attention', tone: 'red' },
};

const METRIC_ORDER = [
  'applications_pending',
  'customers_total',
  'subscriptions_active',
  'mandates_pending',
  'billing_overdue',
] as const;

const ATTENTION_ICONS: Readonly<Record<string, IconName>> = {
  APPLICATIONS_AWAITING_REVIEW: 'application',
  MANDATES_AWAITING_ACTIVATION: 'mandate',
  SCHEDULES_OVERDUE: 'billing',
  NOTIFICATIONS_FAILED: 'alert',
  NOTIFICATIONS_QUEUED: 'submit',
  DISCOUNTS_NEED_REVIEW: 'review',
  PAYMENT_ATTEMPTS_FAILED: 'billing',
  PAYMENT_ATTEMPTS_PROCESSING: 'collections',
  BILLING_AWAITING_AUTHORIZATION: 'calendar',
  NOTIFICATIONS_STALE_PROCESSING: 'alert',
  MANDATE_CANCELLATIONS_OPEN: 'alert',
};

const ACTION_DESCRIPTIONS: Readonly<Record<string, string>> = {
  APPLICATIONS_AWAITING_REVIEW: 'Review submitted details and record the next decision.',
  MANDATES_AWAITING_ACTIVATION: 'Check provider status so repayments can begin.',
  SCHEDULES_OVERDUE: 'Follow up on schedules that have passed their due date.',
  NOTIFICATIONS_FAILED: 'Review failed deliveries and retry where appropriate.',
  NOTIFICATIONS_QUEUED: 'Monitor customer messages still waiting to send.',
  DISCOUNTS_NEED_REVIEW: 'Confirm discount evidence before the application progresses.',
  PAYMENT_ATTEMPTS_FAILED: 'Inspect failed debit attempts and their next step.',
  PAYMENT_ATTEMPTS_PROCESSING: 'Check debit attempts still processing.',
  BILLING_AWAITING_AUTHORIZATION: 'Review repayments waiting on customer authorization.',
  NOTIFICATIONS_STALE_PROCESSING: 'Inspect deliveries that have remained in processing.',
  MANDATE_CANCELLATIONS_OPEN: 'Review open customer cancellation requests.',
};

interface GuidanceLink {
  to: string;
  label: string;
  description: string;
  icon: IconName;
  permission: PermissionCode;
}

const GUIDANCE_LINKS: readonly GuidanceLink[] = [
  {
    to: paths.adminApplications,
    label: 'Applications',
    description: 'Review customer applications and decisions.',
    icon: 'application',
    permission: PERMISSIONS.SUBSCRIPTIONS_READ,
  },
  {
    to: paths.adminMandates,
    label: 'Mandates',
    description: 'Track direct debit setup and activation.',
    icon: 'mandate',
    permission: PERMISSIONS.MANDATES_READ,
  },
  {
    to: paths.adminBilling,
    label: 'Billing & repayments',
    description: 'Follow schedules, collections and overdue items.',
    icon: 'billing',
    permission: PERMISSIONS.BILLING_READ,
  },
  {
    to: paths.adminReports,
    label: 'Reports',
    description: 'Review current operational reporting.',
    icon: 'activity',
    permission: PERMISSIONS.REPORTS_READ,
  },
  {
    to: paths.adminNotificationOutbox,
    label: 'Notifications',
    description: 'Inspect customer delivery queues.',
    icon: 'submit',
    permission: PERMISSIONS.NOTIFICATIONS_READ,
  },
  {
    to: paths.adminSystemHealth,
    label: 'System health',
    description: 'Review dependency and environment health.',
    icon: 'review',
    permission: PERMISSIONS.SYSTEM_HEALTH_READ,
  },
];

const PRIMARY_PIPELINE = [
  { status: 'DRAFT', label: 'Draft', icon: 'draft' },
  { status: 'SUBMITTED', label: 'Submitted', icon: 'submit' },
  { status: 'UNDER_REVIEW', label: 'Under Review', icon: 'review' },
  { status: 'CHANGES_REQUESTED', label: 'Changes Requested', icon: 'alert' },
  { status: 'APPROVED', label: 'Approved', icon: 'check' },
  { status: 'ACCEPTED', label: 'Contract Accepted', icon: 'contract' },
  { status: 'ACTIVE', label: 'Active', icon: 'spark' },
  { status: 'COMPLETED', label: 'Completed', icon: 'flag' },
] as const satisfies readonly { status: string; label: string; icon: IconName }[];

const SUPPLEMENTARY_PIPELINE = [
  { status: 'AWAITING_CUSTOMER_ACCEPTANCE', label: 'Awaiting Customer Acceptance' },
  { status: 'AWAITING_MANDATE', label: 'Awaiting mandate' },
  { status: 'MANDATE_ACTIVE', label: 'Mandate active' },
] as const;

function isOverviewSummary(summary: unknown): summary is OverviewOperationalSummary {
  return (
    typeof summary === 'object' &&
    summary !== null &&
    'customersTotal' in summary &&
    'activeRepaymentContracts' in summary
  );
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'A';
  const first = parts[0] ?? 'A';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  return `${first.charAt(0)}${(parts[1] ?? '').charAt(0)}`.toUpperCase();
}

function metricToneClass(tone: MetricPresentation['tone']): string {
  let className: string | undefined;

  switch (tone) {
    case 'blue':
      className = styles.toneBlue;
      break;
    case 'amber':
      className = styles.toneAmber;
      break;
    case 'red':
      className = styles.toneRed;
      break;
    case 'violet':
      className = styles.toneViolet;
      break;
    default:
      className = styles.toneGreen;
  }

  if (className === undefined) {
    throw new Error(`Missing CSS module class for metric tone: ${tone}`);
  }

  return className;
}

function presentationForMetric(card: DashboardMetricCard): MetricPresentation {
  return (
    METRIC_PRESENTATION[card.key] ?? {
      icon: 'activity',
      support: 'Current portfolio count',
      tone: 'green',
    }
  );
}

function formatActivityDescription(description: string): string {
  return description.includes('_')
    ? description
        .split(' → ')
        .map((part) => humanizeStage(part))
        .join(' → ')
    : description;
}

export function AdminOverviewDashboard({
  generatedAt,
  cards,
  summary,
  pipeline,
  financial,
  attention,
  recent,
  userEmail,
  roleLabel,
  can,
  navigation,
}: {
  generatedAt: string;
  cards: readonly DashboardMetricCard[];
  summary?: OverviewOperationalSummary | SalesOperationalExtras | SupportOperationalExtras;
  pipeline?: ApplicationPipelineView;
  financial?: FinancialSnapshotView | null;
  attention?: readonly AttentionItemView[];
  recent?: readonly AdminActivityItemView[];
  userEmail: string | undefined;
  roleLabel: string;
  can: (permission: PermissionCode) => boolean;
  navigation?: ReactNode;
}): React.JSX.Element {
  const displayName = displayNameFromEmail(userEmail);
  const overviewSummary = isOverviewSummary(summary) ? summary : undefined;
  const metricCards = [...cards]
    .sort((left, right) => {
      const leftIndex = METRIC_ORDER.indexOf(left.key as (typeof METRIC_ORDER)[number]);
      const rightIndex = METRIC_ORDER.indexOf(right.key as (typeof METRIC_ORDER)[number]);
      return (leftIndex < 0 ? 99 : leftIndex) - (rightIndex < 0 ? 99 : rightIndex);
    })
    .slice(0, 5);
  const attentionItems = attention ?? [];
  const recentItems = (recent ?? []).slice(0, 5);
  const pipelineStages = pipeline?.stages ?? [];
  const guidance = GUIDANCE_LINKS.filter((link) => can(link.permission)).slice(0, 2);
  const stageCount = (status: string): number | '—' =>
    pipelineStages.find((stage) => stage.status === status)?.count ?? '—';
  const collectionRate = collectionRatePresentation(financial?.collectionRate);
  const nextActions = attentionItems
    .map((item) => {
      const to = resolveAdminDeepLink(item.route, item.routeQuery);
      return to === undefined ? null : { ...item, to };
    })
    .filter((item): item is AttentionItemView & { to: string } => item !== null)
    .slice(0, 4);

  return (
    <div className={styles.overview}>
      <header className={styles.pageHeader}>
        <div className={styles.greetingBlock}>
          <h1 className={styles.greeting}>
            {formatGreeting()}, {displayName} <span aria-hidden="true">👋</span>
          </h1>
          <p className={styles.subtitle}>
            Here’s what’s happening across your solar operations today.
          </p>
        </div>
        {navigation !== undefined && <div className={styles.headerNavigation}>{navigation}</div>}
        <div className={styles.profileChip} aria-label="Signed-in staff profile">
          <span className={styles.avatar} aria-hidden="true">
            {initialsFromName(displayName)}
          </span>
          <span className={styles.profileMeta}>
            <span className={styles.profileName}>{displayName}</span>
            <span className={styles.profileRole}>{roleLabel}</span>
          </span>
        </div>
      </header>

      <section className={styles.attentionHero} aria-labelledby="overview-attention-heading">
        <div className={styles.heroIntro}>
          <span className={styles.heroIcon}>
            <OverviewIcon name="spark" />
          </span>
          <div>
            <h2 className={styles.attentionTitle} id="overview-attention-heading">
              What needs your attention today
            </h2>
            <p>Start with these live queues to keep customer journeys moving.</p>
          </div>
        </div>
        <div className={styles.attentionArea}>
          {attentionItems.length === 0 ? (
            <div className={styles.attentionEmpty}>
              <span className={styles.attentionIcon}>
                <OverviewIcon name="check" />
              </span>
              <span>
                <strong>You’re all caught up.</strong>
                <small>No permission-visible queues currently need action.</small>
              </span>
            </div>
          ) : (
            <div className={styles.attentionGrid}>
              {attentionItems.map((item) => {
                const to = resolveAdminDeepLink(item.route, item.routeQuery);
                const body = (
                  <>
                    <span className={styles.attentionIcon}>
                      <OverviewIcon name={ATTENTION_ICONS[item.code] ?? 'alert'} />
                    </span>
                    <span className={styles.attentionBody}>
                      <strong>{item.count}</strong>
                      <small>{item.label}</small>
                    </span>
                  </>
                );
                return to === undefined ? (
                  <div className={styles.attentionItem} key={item.code}>
                    {body}
                  </div>
                ) : (
                  <Link className={styles.attentionItem} key={item.code} to={to}>
                    {body}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
        <div className={styles.attentionArt} aria-hidden="true">
          <img src={houseHeroUrl} alt="" decoding="async" />
          <span>Cleaner energy for brighter Nigeria</span>
        </div>
      </section>

      <section aria-labelledby="overview-metrics-heading">
        <h2 className="visually-hidden" id="overview-metrics-heading">
          Overview metrics
        </h2>
        <div className={styles.metricStrip}>
          {metricCards.map((card) => {
            const to = resolveAdminDeepLink(card.filterPath, card.filterQuery);
            const presentation = presentationForMetric(card);
            const content = (
              <>
                <span className={`${styles.metricIcon} ${metricToneClass(presentation.tone)}`}>
                  <OverviewIcon name={presentation.icon} />
                </span>
                <span className={styles.metricCopy}>
                  <strong>{card.value}</strong>
                  <span>{card.label}</span>
                  <small>{presentation.support}</small>
                </span>
                {to !== undefined && (
                  <span className={styles.metricArrow}>
                    <OverviewIcon name="arrow" />
                  </span>
                )}
              </>
            );
            return to === undefined ? (
              <article className={styles.metricCard} key={card.key}>
                {content}
              </article>
            ) : (
              <Link className={styles.metricCard} key={card.key} to={to}>
                {content}
              </Link>
            );
          })}
        </div>
      </section>

      <div className={styles.midRow}>
        <section
          className={`${styles.card} ${styles.pipelineCard}`}
          aria-labelledby="overview-pipeline-heading"
        >
          <div className={styles.cardHeader}>
            <span className={styles.sectionIcon}>
              <OverviewIcon name="application" />
            </span>
            <div>
              <h2 className={styles.cardTitle} id="overview-pipeline-heading">
                Application pipeline
              </h2>
              <p className={styles.cardHint}>
                {pipeline === undefined
                  ? 'Pipeline unavailable'
                  : `${pipeline.pipelineTotal} in active lifecycle stages`}
              </p>
            </div>
          </div>
          {pipelineStages.length === 0 ? (
            <p className={styles.activityEmpty}>No pipeline stages returned.</p>
          ) : (
            <>
              <div className={styles.pipelineScroll}>
                <div className={styles.pipeline} role="list">
                  {PRIMARY_PIPELINE.map((stage, index) => (
                    <div className={styles.pipelineStage} role="listitem" key={stage.status}>
                      {index < PRIMARY_PIPELINE.length - 1 && (
                        <span className={styles.pipelineConnector} aria-hidden="true" />
                      )}
                      <span
                        className={`${styles.pipelineNode} ${stage.status === 'CHANGES_REQUESTED' ? styles.pipelineNodeWarn : ''}`}
                      >
                        <OverviewIcon name={stage.icon} />
                      </span>
                      <strong>{stageCount(stage.status)}</strong>
                      <span>{stage.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.pipelineMeta}>
                <div
                  className={styles.supplementaryStages}
                  aria-label="Intermediate application stages"
                >
                  {SUPPLEMENTARY_PIPELINE.map((stage) => (
                    <span key={stage.status}>
                      <strong>{stageCount(stage.status)}</strong> {stage.label}
                    </span>
                  ))}
                </div>
                {pipeline !== undefined && pipeline.excludedFromPipeline.length > 0 && (
                  <p>
                    Excluded from pipeline:{' '}
                    {pipeline.excludedFromPipeline
                      .map((item) => `${humanizeStage(item.status)} (${item.count})`)
                      .join(' · ')}
                  </p>
                )}
              </div>
            </>
          )}
        </section>

        <section
          className={`${styles.card} ${styles.financialCard}`}
          aria-labelledby="overview-financial-heading"
        >
          <div className={styles.cardHeader}>
            <span className={styles.sectionIcon}>
              <OverviewIcon
                name={financial === null || financial === undefined ? 'activity' : 'collections'}
              />
            </span>
            <div>
              <h2 className={styles.cardTitle} id="overview-financial-heading">
                {financial === null || financial === undefined
                  ? 'Operational snapshot'
                  : 'Collections & repayments'}
              </h2>
              <p className={styles.cardHint}>
                {financial === null || financial === undefined
                  ? 'Current portfolio operations'
                  : 'Current portfolio'}
              </p>
            </div>
          </div>
          {financial === null || financial === undefined ? (
            <div className={styles.financialGrid}>
              <div>
                <span>Registered customers</span>
                <strong>{overviewSummary?.customersTotal ?? '—'}</strong>
              </div>
              <div>
                <span>Active repayment contracts</span>
                <strong>{overviewSummary?.activeRepaymentContracts ?? '—'}</strong>
              </div>
              <div>
                <span>Mandate-active contracts</span>
                <strong>{overviewSummary?.mandateActiveContracts ?? '—'}</strong>
              </div>
              <div>
                <span>Overdue schedules</span>
                <strong>{overviewSummary?.overdueSchedules ?? '—'}</strong>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.financialGrid}>
                <div>
                  <span>Collected</span>
                  <strong>{formatNairaAmount(financial.collectedAmount)}</strong>
                  <small>{financial.collectedCount} settled</small>
                </div>
                <div>
                  <span>Due</span>
                  <strong>{formatNairaAmount(financial.dueAmount)}</strong>
                  <small>{financial.dueCount} scheduled</small>
                </div>
                <div>
                  <span>Overdue</span>
                  <strong>{formatNairaAmount(financial.overdueAmount)}</strong>
                  <small>
                    {financial.overdueCount} schedule{financial.overdueCount === 1 ? '' : 's'}
                  </small>
                </div>
                <div>
                  <span>Collection rate</span>
                  <strong>{collectionRate.label}</strong>
                  <small>Current portfolio</small>
                </div>
              </div>
              <div
                className={styles.rateTrack}
                aria-label={
                  collectionRate.progressPercent === null
                    ? 'Collection rate unavailable'
                    : 'Collection rate visualization'
                }
              >
                {collectionRate.progressPercent !== null && (
                  <span style={{ width: `${collectionRate.progressPercent}%` }} />
                )}
              </div>
            </>
          )}
        </section>
      </div>

      <div className={styles.lowerRow}>
        <section
          className={`${styles.card} ${styles.recentCard}`}
          aria-labelledby="overview-recent-heading"
        >
          <div className={styles.cardHeader}>
            <span className={styles.sectionIcon}>
              <OverviewIcon name="activity" />
            </span>
            <div>
              <h2 className={styles.cardTitle} id="overview-recent-heading">
                Recent activity
              </h2>
              <p className={styles.cardHint}>Latest permission-visible operational events</p>
            </div>
          </div>
          {recentItems.length === 0 ? (
            <p className={styles.activityEmpty}>No recent operational activity.</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.activityTable}>
                <thead>
                  <tr>
                    <th scope="col">Activity</th>
                    <th scope="col">When</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recentItems.map((item) => {
                    const to = resolveAdminDeepLink(item.route ?? undefined);
                    return (
                      <tr key={item.id}>
                        <td>
                          <span className={styles.activityCell}>
                            <span className={styles.activityTypeIcon}>
                              <OverviewIcon name="activity" />
                            </span>
                            <span>
                              <strong>{item.title}</strong>
                              {item.description !== null && item.description !== '' && (
                                <small>{formatActivityDescription(item.description)}</small>
                              )}
                              <small className={styles.entityType}>{item.entityType}</small>
                            </span>
                          </span>
                        </td>
                        <td>{formatRelativeTime(item.occurredAt)}</td>
                        <td>{to === undefined ? '—' : <Link to={to}>Open</Link>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className={styles.rail} aria-label="Insight rail">
          <section
            className={`${styles.card} ${styles.actionsCard}`}
            aria-labelledby="overview-actions-heading"
          >
            <div className={styles.cardHeader}>
              <span className={styles.sectionIcon}>
                <OverviewIcon name="check" />
              </span>
              <div>
                <h2 className={styles.cardTitle} id="overview-actions-heading">
                  Next best actions
                </h2>
                <p className={styles.cardHint}>Focus on the most useful live queues</p>
              </div>
            </div>
            {nextActions.length === 0 ? (
              <p className={styles.activityEmpty}>
                No queued actions for your permissions right now.
              </p>
            ) : (
              <ol className={styles.actionList}>
                {nextActions.map((item, index) => (
                  <li key={item.code}>
                    <span className={styles.actionIndex}>{index + 1}</span>
                    <span className={styles.actionCopy}>
                      <strong>
                        {item.count} {item.label}
                      </strong>
                      <small>
                        {ACTION_DESCRIPTIONS[item.code] ??
                          'Open the live queue and continue the next operational step.'}
                      </small>
                    </span>
                    <Link to={item.to} aria-label="Open →">
                      Open <OverviewIcon name="arrow" />
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section
            className={`${styles.card} ${styles.guidanceCard}`}
            aria-labelledby="overview-guidance-heading"
          >
            <div className={styles.cardHeader}>
              <span className={styles.sectionIcon}>
                <OverviewIcon name="guidance" />
              </span>
              <div>
                <h2 className={styles.cardTitle} id="overview-guidance-heading">
                  System guidance
                </h2>
                <p className={styles.cardHint}>Work areas available to you</p>
              </div>
            </div>
            {guidance.length === 0 ? (
              <p className={styles.activityEmpty}>No additional work areas are available.</p>
            ) : (
              <ul className={styles.guidanceList}>
                {guidance.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to}>
                      <span className={styles.guidanceIcon}>
                        <OverviewIcon name={link.icon} />
                      </span>
                      <span>
                        <strong>{link.label}</strong>
                        <small>{link.description}</small>
                      </span>
                      <span className={styles.guidanceArrow}>
                        <OverviewIcon name="arrow" />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>

      <p className={styles.footerNote}>Updated {new Date(generatedAt).toLocaleString()}</p>
    </div>
  );
}
